import { UTCDate } from "@date-fns/utc"
import { differenceInYears, setHours, setMilliseconds, setMinutes, setSeconds } from "date-fns"

export function getAge(birthDate: Date) {
  return differenceInYears(new Date(), birthDate)
}

/**
 * Utilidades para manejar fechas UTC sin conversión de zona horaria
 */

/**
 * Extrae solo la fecha (DD/MM/YYYY) de la cadena UTC
 * @param dateString - Cadena de fecha en formato UTC (ej: "2025-09-29T10:00:00.000+00:00")
 * @returns Fecha formateada como DD/MM/YYYY
 */
export function formatUTCDate(dateString: string): string {
  // Extraer solo la fecha (YYYY-MM-DD) de la cadena UTC
  const parts = dateString.split("T")
  if (parts.length < 1)
    return ""

  const datePart = parts[0] ?? ""
  if (!datePart)
    return ""

  const [year, month, day] = datePart.split("-")
  if (!year || !month || !day)
    return ""

  return `${day}/${month}/${year}`
}

/**
 * Extrae solo la hora (HH:MM) de la cadena UTC
 * @param dateString - Cadena de fecha en formato UTC (ej: "2025-09-29T10:00:00.000+00:00")
 * @returns Hora formateada como HH:MM
 */
export function formatUTCTime(dateString: string): string {
  // Extraer solo la hora (HH:MM) de la cadena UTC
  const parts = dateString.split("T")
  if (parts.length < 2)
    return ""

  const timePart = parts[1] ?? ""
  if (!timePart)
    return ""

  const [hours, minutes] = timePart.split(":")
  if (!hours || !minutes)
    return ""

  return `${hours}:${minutes}`
}

/**
 * Calcula la duración entre dos fechas UTC sin conversión de zona horaria
 * @param startDateString - Fecha de inicio en formato UTC
 * @param endDateString - Fecha de fin en formato UTC
 * @returns Duración formateada (ej: "2h 30m")
 */
export function calculateUTCDuration(startDateString: string, endDateString: string): string {
  // Calcular duración usando las fechas UTC sin conversión de zona horaria
  const start = new Date(startDateString)
  const end = new Date(endDateString)
  const diffMs = end.getTime() - start.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  let duration = ""
  if (diffHours > 0)
    duration += `${diffHours}h `
  duration += `${diffMinutes}m`
  return duration
}

/**
 * Extrae fecha local de cadena UTC (sin conversión de zona horaria)
 * @param dateString - Cadena de fecha en formato UTC o formato simple YYYY-MM-DD
 * @returns Objeto Date local con fecha y hora extraídas de UTC
 */
export function extractUTCDateAsLocal(dateString: string): Date | undefined {
  // Si es solo fecha sin tiempo (formato YYYY-MM-DD)
  if (!dateString.includes("T")) {
    const [year, month, day] = dateString.split("-")
    if (!year || !month || !day)
      return undefined

    // Crear fecha local usando solo fecha (hora 00:00:00)
    const localDate = new Date(
      Number.parseInt(year),
      Number.parseInt(month) - 1,
      Number.parseInt(day),
      0,
      0,
      0,
      0,
    )

    return localDate
  }

  // Procesar fecha con tiempo (formato UTC)
  const parts = dateString.split("T")
  if (parts.length < 2)
    return undefined

  const datePart = parts[0] ?? ""
  if (!datePart)
    return undefined

  const timePart = parts[1] ?? ""
  if (!timePart)
    return undefined

  // Extraer solo la parte de tiempo antes del +00:00
  const timeOnly = timePart.split("+")[0] ?? ""
  if (!timeOnly)
    return undefined

  const [year, month, day] = datePart.split("-")
  if (!year || !month || !day)
    return undefined

  const [hours, minutes, seconds] = timeOnly.split(":")
  if (!hours || !minutes)
    return undefined

  // Crear fecha local usando fecha y hora (sin conversión de zona horaria)
  const localDate = new Date(
    Number.parseInt(year),
    Number.parseInt(month) - 1,
    Number.parseInt(day),
    Number.parseInt(hours),
    Number.parseInt(minutes),
    seconds ? Number.parseInt(seconds) : 0,
  )

  return localDate
}

/**
 * Extrae hora de cadena UTC como string
 * @param date - Objeto Date o string de fecha
 * @returns Hora formateada como HH:MM
 */
export function extractUTCTimeAsString(date: Date | string | undefined): string {
  if (!date)
    return ""

  let dateString: string
  if (typeof date === "string") {
    dateString = date
  }
  else {
    dateString = date.toISOString()
  }

  // Extraer solo la hora (HH:MM) de la cadena UTC
  const parts = dateString.split("T")
  if (parts.length < 2)
    return ""

  const timePart = parts[1] ?? ""
  if (!timePart)
    return ""

  // Extraer solo la parte de tiempo antes del +00:00 o Z
  const timeOnly = timePart.split("+")[0]?.split("Z")[0] ?? ""
  if (!timeOnly)
    return ""

  const [hours, minutes] = timeOnly.split(":")
  if (!hours || !minutes)
    return ""

  return `${hours}:${minutes}`
}

/**
 * Crea un objeto Date UTC sin conversión de zona horaria
 * @param date - Fecha base
 * @param timeString - Hora en formato HH:MM
 * @returns Objeto Date UTC
 */
export function createUTCDate(date: Date, timeString?: string): Date {
  if (!timeString) {
    // Si no hay hora, usar 00:00
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0))
  }

  const [hours, minutes] = timeString.split(":").map(Number)
  if ((hours === undefined || Number.isNaN(hours)) || (minutes === undefined || Number.isNaN(minutes))) {
    console.error("Invalid time format:", timeString)
    return new Date()
  }

  // Crear fecha UTC usando Date.UTC para evitar conversión de zona horaria
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()

  return new Date(Date.UTC(year, month, day, hours, minutes, 0, 0))
}

/**
 * Crea una fecha sin conversión de zona horaria desde un string o Date
 * @param dateInput - String de fecha o objeto Date
 * @returns Objeto Date sin conversión de zona horaria
 */
export function createDateWithoutTimezone(dateInput: string | Date | undefined): Date | undefined {
  if (!dateInput)
    return undefined

  if (typeof dateInput === "string") {
    return extractUTCDateAsLocal(dateInput)
  }

  // Si ya es un Date, crear uno nuevo sin conversión de zona horaria
  const year = dateInput.getFullYear()
  const month = dateInput.getMonth()
  const day = dateInput.getDate()
  const hours = dateInput.getHours()
  const minutes = dateInput.getMinutes()
  const seconds = dateInput.getSeconds()

  return new Date(year, month, day, hours, minutes, seconds)
}

/**
 * Crea una fecha desde un string en formato YYYY-MM-DD sin conversión de zona horaria
 * @param dateString - String de fecha en formato YYYY-MM-DD
 * @returns Objeto Date sin conversión de zona horaria
 */
export function createDateFromString(dateString: string): Date | undefined {
  if (!dateString)
    return undefined

  const [year, month, day] = dateString.split("-")
  if (!year || !month || !day)
    return undefined

  // Crear fecha local usando solo fecha (hora 00:00:00)
  const localDate = new Date(
    Number.parseInt(year),
    Number.parseInt(month) - 1,
    Number.parseInt(day),
    0,
    0,
    0,
    0,
  )

  return localDate
}

export function overrideTime(date: Date, time?: string): Date {
  if (!time)
    return date
  const [hours, minutes] = time.split(":").map(Number)
  if ((hours === undefined || Number.isNaN(hours)) || (minutes === undefined || Number.isNaN(minutes))) {
    console.error("Invalid time format:", time)
    return date
  }
  return new UTCDate(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0)
}

/**
 * Formats time from Date object to HH:MM string
 * @param date - Date object to extract time from
 * @returns Time formatted as HH:MM
 */
export function formatTimeFromDate(date: Date | undefined): string {
  if (!date)
    return "00:00"

  const hours = date.getHours().toString().padStart(2, "0")
  const minutes = date.getMinutes().toString().padStart(2, "0")

  return `${hours}:${minutes}`
}

export function setToNoon(date: Date): Date {
  return setMilliseconds(setSeconds(setMinutes(setHours(date, 12), 0), 0), 0)
}
